import { useState, useEffect } from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Search,
  Plus,
  MessageSquare,
  Clock,
  ChevronDown,
  ChevronUp,
  X,
  HelpCircle,
  ExternalLink,
  Send,
  Link2,
  Loader2,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { THREAD_CATEGORIES, THREAD_TAGS } from '@/data/forumData';
import type { ThreadCategory } from '@/data/forumData';
import { cn } from '@/lib/utils';
import ModuleOnboarding from '@/components/ModuleOnboarding';
import { forumOnboardingSteps, forumOnboardingBenefits } from '@/data/onboardingConfigs';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  useForumThreads,
  useCreateThread,
  useCreateResponse,
  type ForumThreadRow,
} from '@/hooks/useForum';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

// Small helper to get response data for expanded threads
import { useForumResponses } from '@/hooks/useForum';

function ThreadResponses({ threadId, currentUserId }: { threadId: string; currentUserId: string | undefined }) {
  const { data: responses, isLoading } = useForumResponses(threadId);
  const createResponse = useCreateResponse();
  const [newComment, setNewComment] = useState('');

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    createResponse.mutate(
      { threadId, content: newComment.trim() },
      { onSuccess: () => { setNewComment(''); toast.success('Comentario publicado'); } },
    );
  };

  return (
    <div className="mt-4 pt-4 border-t border-border">
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : responses && responses.length > 0 ? (
        <ScrollArea className="max-h-80">
          <div className="space-y-3 pr-2">
            {responses.map((response) => (
              <div
                key={response.id}
                className={cn(
                  'flex gap-3 p-3 rounded-lg',
                  response.is_solution ? 'bg-emerald-500/10 border border-emerald-200' : 'bg-muted/50',
                )}
              >
                <Avatar className="w-8 h-8 shrink-0">
                  <AvatarImage src={response.author?.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">
                    {response.author?.full_name
                      ? response.author.full_name.split(' ').map((n) => n[0]).join('')
                      : <User className="w-4 h-4 text-muted-foreground" />}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{response.author?.full_name || 'Anónimo'}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(parseISO(response.created_at), { addSuffix: true, locale: es })}
                    </span>
                    {response.is_solution && (
                      <Badge className="text-xs bg-emerald-500/20 text-emerald-600 border-emerald-200">✓ Útil</Badge>
                    )}
                  </div>
                  <p className="text-sm text-foreground mt-1">{response.content}</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-4">Sé el primero en comentar</p>
      )}

      {/* Add Comment */}
      <div className="flex gap-2 mt-4">
        <div className="flex-1 flex gap-2">
          <Input
            placeholder="Escribe un comentario..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newComment.trim()) handleAddComment();
            }}
          />
          <Button size="icon" onClick={handleAddComment} disabled={!newComment.trim() || createResponse.isPending}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ForumPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const threadParam = searchParams.get('thread');
  const { data: threads, isLoading } = useForumThreads();
  const createThread = useCreateThread();

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(threadParam);

  // Auto-scroll to the thread from query param once threads load
  useEffect(() => {
    if (threadParam && threads && threads.length > 0) {
      setExpandedThreadId(threadParam);
      setTimeout(() => {
        const el = document.getElementById(`thread-${threadParam}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [threadParam, threads]);
  const [newThread, setNewThread] = useState({
    title: '',
    description: '',
    category: '' as ThreadCategory | '',
    tags: [] as string[],
    externalLink: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [availableTags] = useState<string[]>(() => Object.values(THREAD_TAGS).map((t) => t.label));
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('forum_onboarding_seen'));

  const handleOnboardingComplete = () => {
    localStorage.setItem('forum_onboarding_seen', 'true');
    setShowOnboarding(false);
  };

  const filteredThreads = (threads || []).filter((thread) => {
    const matchesSearch =
      searchQuery === '' ||
      thread.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (thread.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || thread.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter]);
  const totalPages = Math.max(1, Math.ceil(filteredThreads.length / itemsPerPage));
  const paginatedThreads = filteredThreads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleCreateThread = () => {
    if (!newThread.title || !newThread.description || !newThread.category) return;

    createThread.mutate(
      {
        title: newThread.title,
        description: newThread.description,
        category: newThread.category as string,
        tags: newThread.tags,
        external_link: newThread.externalLink || undefined,
      },
      {
        onSuccess: () => {
          setCreateDialogOpen(false);
          setNewThread({ title: '', description: '', category: '', tags: [], externalLink: '' });
          setTagInput('');
          toast.success('Post publicado');
        },
      },
    );
  };

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !newThread.tags.includes(trimmed)) {
      setNewThread((prev) => ({ ...prev, tags: [...prev.tags, trimmed] }));
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove: string) => {
    setNewThread((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tagToRemove) }));
  };

  const filteredSuggestions = tagInput.trim()
    ? availableTags.filter((tag) => tag.toLowerCase().includes(tagInput.toLowerCase()) && !newThread.tags.includes(tag))
    : [];

  if (showOnboarding) {
    return (
      <ModuleOnboarding
        onComplete={handleOnboardingComplete}
        steps={forumOnboardingSteps}
        benefits={forumOnboardingBenefits}
        ctaText="Explorar la Comunidad"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">Comunidad</h1>
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground" onClick={() => setShowOnboarding(true)} title="Ver ayuda">
              <HelpCircle className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-muted-foreground mt-1">Colabora, pregunta y comparte conocimiento con otros participantes</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="gap-2">
              <Plus className="w-4 h-4" />
              Nuevo Post
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Crear nuevo post</DialogTitle>
              <DialogDescription>Comparte tu pregunta, idea o aporte con la comunidad</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título</Label>
                <Input id="title" placeholder="Escribe un título descriptivo..." value={newThread.title} onChange={(e) => setNewThread({ ...newThread, title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea id="description" placeholder="Describe tu pregunta o tema en detalle..." rows={5} value={newThread.description} onChange={(e) => setNewThread({ ...newThread, description: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={newThread.category} onValueChange={(value) => setNewThread({ ...newThread, category: value as ThreadCategory })}>
                  <SelectTrigger><SelectValue placeholder="Selecciona una categoría" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(THREAD_CATEGORIES).map(([key, { label, icon }]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2"><span>{icon}</span><span>{label}</span></span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Etiquetas</Label>
                {newThread.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {newThread.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)} className="ml-1 rounded-full hover:bg-muted p-0.5"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Input placeholder="Escribe para buscar o agregar etiquetas..." value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && tagInput.trim()) { e.preventDefault(); addTag(tagInput); } }} />
                  {filteredSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                      {filteredSuggestions.map((tag) => (
                        <button key={tag} type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors" onClick={() => addTag(tag)}>{tag}</button>
                      ))}
                    </div>
                  )}
                  {tagInput.trim() && !availableTags.some((t) => t.toLowerCase() === tagInput.toLowerCase()) && (
                    <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-md shadow-lg">
                      <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2" onClick={() => addTag(tagInput)}>
                        <Plus className="w-4 h-4 text-primary" />
                        <span>Crear etiqueta: <strong>"{tagInput.trim()}"</strong></span>
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Presiona Enter para agregar una nueva etiqueta</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="externalLink" className="flex items-center gap-2"><Link2 className="w-4 h-4" />Enlace externo (opcional)</Label>
                <Input id="externalLink" type="url" placeholder="https://ejemplo.com/recurso" value={newThread.externalLink} onChange={(e) => setNewThread({ ...newThread, externalLink: e.target.value })} />
                <p className="text-xs text-muted-foreground">Agrega un enlace a un recurso, artículo o herramienta relacionada</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateThread} disabled={!newThread.title || !newThread.description || !newThread.category || createThread.isPending}>
                {createThread.isPending ? 'Publicando...' : 'Publicar post'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Category Filter */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Buscar posts..." className="pl-10" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant={categoryFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setCategoryFilter('all')} className="rounded-full">Todos</Button>
          {Object.entries(THREAD_CATEGORIES).map(([key, { label, icon }]) => (
            <Button key={key} variant={categoryFilter === key ? 'default' : 'outline'} size="sm" onClick={() => setCategoryFilter(key)} className="rounded-full gap-1.5">
              <span>{icon}</span>{label}
            </Button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        <span>{filteredThreads.length} posts encontrados</span>
      </div>

      {/* Thread List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredThreads.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-medium">No se encontraron posts</h3>
              <p className="text-muted-foreground mt-1">
                {categoryFilter !== 'all' ? 'Intenta seleccionar otra categoría' : '¡Sé el primero en publicar!'}
              </p>
            </CardContent>
          </Card>
        ) : (
          paginatedThreads.map((thread) => {
            const isExpanded = expandedThreadId === thread.id;
            const category = thread.category as keyof typeof THREAD_CATEGORIES;

            return (
              <Card key={thread.id} id={`thread-${thread.id}`} className={cn('transition-all duration-300', isExpanded && 'ring-2 ring-primary/20')}>
                <CardContent className="p-4">
                  <div className="flex gap-4 cursor-pointer" onClick={() => setExpandedThreadId(isExpanded ? null : thread.id)}>
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarImage src={thread.author?.avatar_url || undefined} />
                      <AvatarFallback>{thread.author?.full_name ? thread.author.full_name.split(' ').map((n) => n[0]).join('') : <User className="w-5 h-5 text-muted-foreground" />}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-2 sm:line-clamp-1">{thread.title}</h3>
                          <p className={cn('text-sm text-muted-foreground mt-1', !isExpanded && 'line-clamp-2')}>{thread.description}</p>
                          {thread.external_link && (
                            <a href={thread.external_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2">
                              <ExternalLink className="w-3.5 h-3.5" />Ver enlace externo
                            </a>
                          )}
                        </div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {THREAD_CATEGORIES[category] && (
                          <Badge variant="outline" className={cn('text-xs', THREAD_CATEGORIES[category].color)}>
                            {THREAD_CATEGORIES[category].icon} {THREAD_CATEGORIES[category].label}
                          </Badge>
                        )}
                        {(thread.tags || []).slice(0, 2).map((tag) => {
                          const tagKey = tag as keyof typeof THREAD_TAGS;
                          return (
                            <Badge key={tag} variant="outline" className={cn('text-xs', THREAD_TAGS[tagKey]?.color)}>
                              {THREAD_TAGS[tagKey]?.label || tag}
                            </Badge>
                          );
                        })}
                        {(thread.tags || []).length > 2 && <Badge variant="outline" className="text-xs">+{(thread.tags || []).length - 2}</Badge>}
                        <span className="text-muted-foreground">•</span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MessageSquare className="w-3.5 h-3.5" />{thread.response_count} respuestas
                        </span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">
                          {thread.last_activity_at
                            ? formatDistanceToNow(parseISO(thread.last_activity_at), { addSuffix: true, locale: es })
                            : formatDistanceToNow(parseISO(thread.created_at), { addSuffix: true, locale: es })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <span>{thread.author?.full_name || 'Anónimo'}</span>
                        <span>•</span>
                        <span>{thread.author?.company || ''}</span>
                      </div>
                    </div>
                  </div>

                  {isExpanded && <ThreadResponses threadId={thread.id} currentUserId={user?.id} />}
                </CardContent>
              </Card>
            );
          })
        )}

        {!isLoading && totalPages > 1 && (
          <Pagination className="pt-4">
            <PaginationContent className="flex-wrap">
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .map((page, idx, arr) => {
                  const prev = arr[idx - 1];
                  const showEllipsis = prev && page - prev > 1;
                  return (
                    <span key={page} className="flex items-center">
                      {showEllipsis && <span className="px-2 text-muted-foreground">…</span>}
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    </span>
                  );
                })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
